import { Children, useRef, type ReactNode } from 'react'
import { Swiper, SwiperSlide, type SwiperClass as SwiperType } from 'swiper/react'
import { A11y, Pagination, FreeMode } from 'swiper/modules'
import { type SwiperOptions, type PaginationOptions } from 'swiper/types'
import { NavButton } from '@/features/shop/components/Carousel/NavButton'
import styled from 'styled-components'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { type SurfaceOption } from '@shipt/design-system-themes'

const modules: SwiperOptions['modules'] = [A11y, Pagination, FreeMode]
const a11y: SwiperOptions['a11y'] = { enabled: true, scrollOnFocus: false }

// update only when the value actually changes since transitionStart fires
// a lot during freeMode momentum, and identical setAttribute calls can
// still invalidate style in some engines.
const setHidden = (element: HTMLElement | null, hidden: boolean) => {
  if (!element) return
  const next = String(hidden)
  element.setAttribute('data-hidden', next)
}

type Props = Omit<SwiperOptions, 'pagination'> & {
  pagination?: PaginationOptions
  title?: string
  className?: string
  onSlideChange?: () => void
  shelfIndex?: number
  children: ReactNode
  surface?: SurfaceOption
  scrollOneCard?: boolean
  store_id?: number
  navButtonOverlayColor?: string
}

export function Carousel({
  children,
  title = '',
  className = '',
  onSlideChange,
  shelfIndex = 0,
  spaceBetween = 16,
  slidesPerView = 'auto',
  speed = 400,
  pagination,
  simulateTouch = false,
  grabCursor = false,
  freeMode = false,
  centeredSlides = false,
  centeredSlidesBounds = false,
  slidesOffsetAfter = 0,
  slidesOffsetBefore = 0,
  surface = 'default',
  scrollOneCard = false,
  breakpoints,
  store_id,
  navButtonOverlayColor,
}: Props) {
  const swiperInstanceRef = useRef<SwiperType | null>(null)
  const leftNavEl = useRef<HTMLDivElement>(null)
  const rightNavEl = useRef<HTMLDivElement>(null)

  // imperative DOM write instead of setState since nav visibility changes
  // never re render this component but its internal state is updated.
  const updateNavStatus = () => {
    const swiper = swiperInstanceRef.current
    if (!swiper || swiper.destroyed) return
    setHidden(leftNavEl.current, swiper.isBeginning)
    setHidden(rightNavEl.current, swiper.isEnd)
  }

  const slidesToScrollForward = () => {
    if (!swiperInstanceRef.current) {
      return 0
    }
    const {
      activeIndex,
      slidesSizesGrid = [],
      params: { spaceBetween: currentSpaceBetween = 0 },
    } = swiperInstanceRef.current
    let { width: widthLeft } = swiperInstanceRef.current

    if ((slidesSizesGrid[0] !== undefined && widthLeft < slidesSizesGrid[0]) || scrollOneCard) {
      return activeIndex + 1
    }

    return slidesSizesGrid.reduce((acc, slideWidth, index) => {
      if (
        acc < index &&
        (widthLeft -= slideWidth + Number(currentSpaceBetween)) >= 0 - Number(currentSpaceBetween)
      ) {
        return index
      }
      return acc
    }, activeIndex)
  }

  const slidesToScrollBackward = () => {
    if (!swiperInstanceRef.current) {
      return 0
    }
    const {
      activeIndex,
      slidesSizesGrid = [],
      params: { spaceBetween: currentSpaceBetween = 0 },
    } = swiperInstanceRef.current
    let { width: widthLeft } = swiperInstanceRef.current

    if ((slidesSizesGrid[0] !== undefined && widthLeft < slidesSizesGrid[0]) || scrollOneCard) {
      return activeIndex - 1
    }

    return slidesSizesGrid.reduceRight((acc, slideWidth, index) => {
      if (
        acc > index &&
        (widthLeft -= slideWidth + Number(currentSpaceBetween)) >= 0 - Number(currentSpaceBetween)
      ) {
        return index
      }
      return acc
    }, activeIndex)
  }

  const onNextClick = () => {
    const nextIndex = slidesToScrollForward()
    swiperInstanceRef.current?.slideTo(nextIndex)
    if (title) {
      trackElementClicked({
        type: 'right_arrow',
        shelf_name: title,
        shelf_index: shelfIndex,
        store_id,
      })
    }
  }

  const onPrevClick = () => {
    const prevIndex = slidesToScrollBackward()
    swiperInstanceRef.current?.slideTo(prevIndex)
    if (title) {
      trackElementClicked({
        type: 'left_arrow',
        shelf_name: title,
        shelf_index: shelfIndex,
        store_id,
      })
    }
  }

  return (
    <Wrapper>
      <CarouselSwiper
        onUpdate={updateNavStatus}
        modules={modules}
        className={`swiper ${className}`}
        a11y={a11y}
        breakpoints={breakpoints}
        centeredSlides={centeredSlides}
        centeredSlidesBounds={centeredSlidesBounds}
        freeMode={freeMode}
        grabCursor={grabCursor}
        onTransitionStart={() => {
          updateNavStatus()
          onSlideChange?.()
        }}
        onResize={updateNavStatus}
        onSwiper={(swiper) => {
          swiperInstanceRef.current = swiper
          updateNavStatus()
        }}
        pagination={pagination}
        simulateTouch={simulateTouch}
        slidesPerView={slidesPerView}
        slidesOffsetAfter={slidesOffsetAfter}
        slidesOffsetBefore={slidesOffsetBefore}
        spaceBetween={spaceBetween}
        speed={speed}
        watchOverflow
      >
        {Children.map(children, (child) => {
          if (child) {
            return <SwiperSlide>{child}</SwiperSlide>
          }
        })}
        {!!pagination && <div className="swiper-pagination" />}
      </CarouselSwiper>
      <NavButton
        ref={leftNavEl}
        surface={surface}
        direction="left"
        onClick={onPrevClick}
        navButtonOverlayColor={navButtonOverlayColor}
      />
      <NavButton
        ref={rightNavEl}
        surface={surface}
        direction="right"
        onClick={onNextClick}
        navButtonOverlayColor={navButtonOverlayColor}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  overflow: visible;
`

const CarouselSwiper = styled(Swiper)`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;

  .swiper-slide {
    display: flex;
    width: auto;
    transform: none;
  }

  .swiper-wrapper {
    display: flex;
    align-items: center;
  }
`
